<template>
  <Transition
    enter-active-class="transition-all duration-700 accelerate subnav-enter-transition"
    enter-from-class="-translate-y-full lg:translate-x-full lg:translate-y-0 subnav-enter-from"
    leave-active-class="transition-all duration-300 ease-out-sine subnav-leave-transition"
    leave-to-class="-translate-y-full lg:translate-x-full lg:translate-y-0 subnav-enter-from"
  >
    <nav v-if="isOpen" :class="menuStyles">
      <div
        ref="subNavRef"
        class="inner absolute inset-0 px-10 pt-[140px] lg:pl-[50px] lg:pr-[110px] lg:pt-[180px] flex flex-col lg:flex-row lg:flex-wrap"
      >
        <div class="flex flex-col lg:flex-row lg:grow lg:basis-2/3">
          <div class="flex flex-col lg:basis-2/4 navLinkContainer">
            <component
              v-for="(link, index) in navlinks"
              :is="getComponentType(link?.linkType!)"
              :ctaLabel="link?.name"
              :ctaLink="getLinkUrl(link)"
              :links="getLinks(link)"
              :index="index"
              :activeIndex="activeIndex"
              :classes="linkStyles"
              :showSubNav="currentSubnavOpen(index)"
              @toggle="openSubNav"
            />
          </div>
          <sub-navigation
            :links="subLinks"
            :isOpen="showSubNav"
            classes="hidden lg:flex lg:basis-2/4"
          />
        </div>

        <div class="navLinkContainer flex flex-col grow-0 mt-20 lg:mt-0 lg:basis-1/3">
          <a
            v-for="link in subNavLinks"
            :href="link.url"
            class="text-white text-nav-s lg:text-l mb-4 font-bold transition-all"
          >
            {{ link.name }}
          </a>
        </div>
        <div class="h-0 lg:basis-full"></div>
        <search-field
          :placeholder="searchFieldLabel"
          classes="mt-20 mb-16 lg:mt-auto lg:mb-28 lg:basis-2/3"
          :subsite="subsite"
          @handle-click="navigateToSearchpage"
        />
      </div>
    </nav>
  </Transition>
</template>

<script setup lang="ts">
import { IDropdownLink, INavLink } from "@/types/components/INavigationProps";
import NavLink from "@organisms/Navigation/NavLink.vue";
import NavButton from "@organisms/Navigation/NavButton.vue";
import SubNavigation from "@organisms/Navigation/SubNavigation.vue";
import SearchField from "@atoms/SearchField/SearchField.vue";
import { computed, ref, toRefs, watch } from "vue";
import { NavLinkType } from "@/util/constants";

interface INavigationDropdownProps {
  isOpen: boolean;
  navlinks?: (IDropdownLink | INavLink | undefined)[];
  subNavLinks?: INavLink[];
  searchFieldLabel: string;
  searchPageUrl: string;
  subsite: string;
}

const props = withDefaults(defineProps<INavigationDropdownProps>(), {});
const {
  navlinks,
  subNavLinks,
  searchFieldLabel,
  searchPageUrl,
  isOpen,
  subsite,
} = toRefs(props);

const showSubNav = ref(false);
const activeIndex = ref(-1);
const subNavRef = ref<HTMLElement | null>(null);

function getComponentType(name: NavLinkType) {
  switch (name) {
    case NavLinkType.DROPDOWN:
      return NavButton;
    case NavLinkType.LINK:
      return NavLink;
  }
}

// to handles ts error => always expecting a number
function openSubNav(...args: unknown[]) {
  const idx = args[0];
  if (typeof idx === "number") {
    if (showSubNav.value && activeIndex.value === idx) {
      activeIndex.value = -1;
      showSubNav.value = false;
    } else if (showSubNav.value) {
      showSubNav.value = false;
      activeIndex.value = idx;
      showSubNav.value = true;
    } else {
      showSubNav.value = true;
      activeIndex.value = idx;
    }
  }
}

function getLinkUrl(link: IDropdownLink | INavLink | undefined): string | undefined {
  if (!link) return;
  if ("url" in link) {
    return link.url;
  }
}

function getLinks(link: IDropdownLink | INavLink | undefined): INavLink[] | undefined {
  if (!link) return;
  if ("links" in link) {
    return link.links;
  }
}

watch(isOpen, (value) => {
  if (!value) {
    showSubNav.value = false;
    activeIndex.value = -1;
  }
});

function currentSubnavOpen(idx: number) {
  return activeIndex.value === idx && showSubNav.value;
}

const navigateToSearchpage = (input: string) => {
  // Construct the new URL with the query parameter
  const searchQueryParam = encodeURIComponent(input);
  const newUrl = `${searchPageUrl.value}?search=${searchQueryParam}`;
  window.location.href = newUrl;
};

const subLinks = computed(() => {
  if (!navlinks?.value) return [];
  const link = navlinks.value[activeIndex.value] as IDropdownLink;
  const subNavLinks = link?.links;
  return subNavLinks;
});

const linkStyles = computed(() => {
  return activeIndex.value === -1 ? "text-white" : "text-white-50 hover:text-white";
});

const menuStyles = computed(() => {
  let bgColor = subsite?.value == "cowiFonden" ? "bg-cowifond-green" : "bg-orange";
  return `${bgColor} fixed w-screen min-h-screen top-0 left-0 z-40`;
});
</script>
